import { useEffect, useRef, forwardRef, useImperativeHandle, useState, useCallback } from 'react';
import { MediaPipeLoader } from '../utils/MediaPipeLoader'; // 导入新的加载器

const HandTracking = forwardRef(({ onGestureChange, onPositionChange, isActive = true }, ref) => {
  const consoleRef = useRef(console);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const handsRef = useRef(null);
  const cameraRef = useRef(null);
  const lastGestureRef = useRef('unknown');
  const lastGestureTimeRef = useRef(0);
  const isInitializedRef = useRef(false);

  const [isCameraReady, setIsCameraReady] = useState(false);

  useImperativeHandle(ref, () => ({
    getVideoElement: () => videoRef.current,
    isReady: () => isCameraReady
  }));

  const getGestureName = (gestureId) => {
    const names = {
      0: 'unknown',
      1: 'fist',
      2: 'open_palm',
      3: 'pointing',
      4: 'peace',
      5: 'thumb_up'
    };
    return names[gestureId] || 'unknown';
  };

  const detectGesture = useCallback((landmarks) => {
    if (!landmarks || landmarks.length < 21) {
      return 'unknown';
    }

    const fingerTips = [8, 12, 16, 20];
    const fingerPips = [6, 10, 14, 18];
    const wrist = landmarks[0];
    const thumbTip = landmarks[4];
    const indexMCP = landmarks[5];
    const middleMCP = landmarks[9];

    let extendedFingers = 0;
    for (let i = 0; i < 4; i++) {
      if (landmarks[fingerTips[i]].y < landmarks[fingerPips[i]].y) {
        extendedFingers++;
      }
    }

    const isThumbExtended = 
      thumbTip.y < indexMCP.y && 
      thumbTip.y < wrist.y;

    if (isThumbExtended && extendedFingers === 4) {
      return 'open_palm';
    }
    if (extendedFingers === 0) {
      return 'fist';
    }
    if (extendedFingers === 2) {
      const indexUp = landmarks[8].y < landmarks[6].y;
      const middleUp = landmarks[12].y < landmarks[10].y;
      if (indexUp && middleUp) {
        return 'peace';
      }
    }
    if (extendedFingers === 1 && landmarks[8].y < landmarks[6].y) {
      return 'pointing';
    }
    if (isThumbExtended && thumbTip.y < middleMCP.y) {
      return 'thumb_up';
    }

    return 'unknown';
  }, []);

  const drawLandmarks = useCallback((ctx, landmarks, width, height) => {
    if (!landmarks || landmarks.length < 21) return;

    const connections = [
      [0, 1], [1, 2], [2, 3], [3, 4],
      [0, 5], [5, 6], [6, 7], [7, 8],
      [0, 9], [9, 10], [10, 11], [11, 12],
      [0, 13], [13, 14], [14, 15], [15, 16],
      [0, 17], [17, 18], [18, 19], [19, 20],
      [5, 9], [9, 13], [13, 17]
    ];

    ctx.strokeStyle = 'rgba(0, 255, 255, 0.8)';
    ctx.lineWidth = 2;

    connections.forEach(([i, j]) => {
      const p1 = landmarks[i];
      const p2 = landmarks[j];
      ctx.beginPath();
      ctx.moveTo(p1.x * width, p1.y * height);
      ctx.lineTo(p2.x * width, p2.y * height);
      ctx.stroke();
    });

    const tipIndices = [4, 8, 12, 16, 20];
    landmarks.forEach((point, index) => {
      const isTip = tipIndices.includes(index);
      
      ctx.beginPath();
      ctx.arc(point.x * width, point.y * height, isTip ? 8 : 5, 0, 2 * Math.PI);
      
      if (isTip) {
        ctx.fillStyle = '#ff00ff';
      } else if (index === 0) {
        ctx.fillStyle = '#00ff00';
      } else {
        ctx.fillStyle = '#00ffff';
      }
      
      ctx.fill();
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 1;
      ctx.stroke();
    });
  }, []);

  const processResults = useCallback((results) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    const ctx = canvas?.getContext('2d');

    if (!canvas || !ctx || !video) return;

    const width = canvas.width;
    const height = canvas.height;

    ctx.save();
    ctx.clearRect(0, 0, width, height);

    if (results.multiHandLandmarks && results.multiHandLandmarks.length > 0) {
      const landmarks = results.multiHandLandmarks[0];
      const gesture = detectGesture(landmarks);
      const now = Date.now();

      drawLandmarks(ctx, landmarks, width, height);

      if (onPositionChange) {
        const palmCenter = landmarks[9];
        onPositionChange({ x: palmCenter.x, y: palmCenter.y });
      }

      const gestureChanged = gesture !== lastGestureRef.current;
      const cooldownPassed = now - lastGestureTimeRef.current > 300;

      if (gestureChanged && cooldownPassed && gesture !== 'unknown') {
        lastGestureRef.current = gesture;
        lastGestureTimeRef.current = now;
        
        if (onGestureChange) {
          onGestureChange(gesture);
        }
      }
    } else {
      ctx.fillStyle = 'rgba(255, 50, 50, 0.2)';
      ctx.fillRect(0, 0, width, height);
      ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
      ctx.font = '18px Arial';
      ctx.textAlign = 'center';
      ctx.fillText('👋 请将手伸入画面', width / 2, height / 2);
    }

    ctx.restore();
  }, [detectGesture, drawLandmarks, onGestureChange, onPositionChange]);

  const initCamera = useCallback(async () => {
    if (isInitializedRef.current) return;

    try {
      // 使用全局作用域检测确保在浏览器环境中执行
      if (typeof window === 'undefined') {
        consoleRef.current.error('仅在浏览器环境中初始化摄像头');
        return;
      }

      // 使用 MediaPipeLoader 来加载必要的模块
      const { Hands, Camera } = await MediaPipeLoader.load();

      // 验证构造函数
      if (typeof Hands !== 'function') {
        throw new Error(`Hands 应该是一个函数，但得到的是 ${typeof Hands}`);
      }
      
      if (typeof Camera !== 'function') {
        throw new Error(`Camera 应该是一个函数，但得到的是 ${typeof Camera}`);
      }

      handsRef.current = new Hands({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/hands/${file}`
      });

      handsRef.current.setOptions({
        maxNumHands: 1,
        modelComplexity: 1,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
      });

      handsRef.current.onResults(processResults);

      if (!videoRef.current) {
        consoleRef.current.error('Video element not ready');
        return;
      }

      cameraRef.current = new Camera(videoRef.current, {
        onFrame: async () => {
          if (videoRef.current && handsRef.current) {
            await handsRef.current.send({ image: videoRef.current });
          }
        },
        width: 640,
        height: 480
      });

      await cameraRef.current.start();
      isInitializedRef.current = true;
      setIsCameraReady(true);
      consoleRef.current.log('📹 摄像头初始化成功');
    } catch (error) {
      consoleRef.current.error('摄像头初始化失败:', error);
      consoleRef.current.error('详细错误信息:', error.message, error.stack);
      consoleRef.current.error('错误名称:', error.name);
    }
  }, [processResults]);

  useEffect(() => {
    if (isActive && !isInitializedRef.current) {
      const timer = setTimeout(() => {
        initCamera();
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [isActive, initCamera]);

  useEffect(() => {
    return () => {
      if (cameraRef.current) {
        cameraRef.current.stop();
      }
    };
  }, []);

  return (
    <div style={{ position: 'fixed', bottom: '20px', right: '20px', zIndex: 100 }}>
      <video
        ref={videoRef}
        style={{
          width: '192px',
          height: '144px',
          objectFit: 'cover',
          borderRadius: '12px',
          border: '2px solid rgba(0, 255, 255, 0.5)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
        }}
        playsInline
        muted
        autoPlay
      />
      <canvas
        ref={canvasRef}
        width="640"
        height="480"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '192px',
          height: '144px',
          borderRadius: '12px',
          pointerEvents: 'none'
        }}
      />
      <div style={{
        position: 'absolute',
        bottom: '8px',
        left: '8px',
        fontSize: '10px',
        color: isCameraReady ? '#00ff00' : '#ff6600',
        textShadow: '0 1px 2px rgba(0,0,0,0.8)'
      }}>
        {isCameraReady ? '● 摄像头已启动' : '○ 初始化中...'}
      </div>
    </div>
  );
});

HandTracking.displayName = 'HandTracking';

export default HandTracking;
